<template>
  <div class="orders" v-if="getOrders">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getOrders"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getOrderCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('orders') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button
              v-if="!isReadOnly"
              class="btn btn-sm btn-inverse"
              @click="$emit('create-order')"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('order_create') }}
            </button>
            <ButtonWait
              :startCallback="startOrdersExport"
              :pollCallback="fetchExportBuildState"
              class="pull-right"
            >
              <template v-slot:buttonText>
                {{ $t('export_view') }}
              </template>
            </ButtonWait>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Order Number -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="canOpenOrderPage && slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'OrderEditPage',
              params: { orderNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
          <span v-else-if="slotProps.data && slotProps.data.number !== null">
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Supplier -->
      <Column
        field="supplier"
        :header="$t('supplier')"
        sortable
        filterMatchMode="contains"
        filterField="supplier"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.supplier">
            <span v-if="slotProps.data.supplier.name">
              {{ slotProps.data.supplier.name }}
            </span>
            <span v-else>
              {{ slotProps.data.supplier }}
            </span>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Supplier Order Number -->
      <Column
        field="supplierOrderNumber"
        :header="$t('supplierOrderNumber')"
        sortable
        filterMatchMode="contains"
        filterField="supplierOrderNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.supplierOrderNumber">
            {{ slotProps.data.supplierOrderNumber }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Project -->
      <Column
        v-if="showProjectColumn"
        field="project"
        :header="$t('project')"
        sortable
        filterField="projectSearch"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.project">
            <router-link
              v-if="slotProps.data.project && slotProps.data.project.number"
              target="_blank"
              :to="{
                name: 'ProjectEditPage',
                params: { projectNumber: slotProps.data.project.number },
              }"
              >{{ slotProps.data.project.number.toString().padStart(4, '0') }}
              <i class="fa fa-sm fa-external-link"></i>
            </router-link>
            &nbsp;-&nbsp;
            {{ slotProps.data.project.customer.firstname }}
            {{ slotProps.data.project.customer.lastname }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <!-- Priority -->
      <Column
        field="priority"
        :header="$t('priority')"
        sortable
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.priority"
            :variant="getOrderPriorityColor(slotProps.data.priority)"
          >
            {{ $t(slotProps.data.priority) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.priorities"
            :options="priorities"
            @input="onPriorityFilter"
            optionLabel="label"
            :placeholder="$t('priority')"
            display="chip"
            :style="{ 'max-width': '120px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOrderPriorityColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Order Type -->
      <Column
        field="orderType"
        :header="$t('orderType')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.orderType"
            :variant="getOrderTypeColor(slotProps.data.orderType)"
          >
            {{ $t(slotProps.data.orderType) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.orderTypes"
            :options="orderTypes"
            @input="onOrderTypeFilter"
            optionLabel="label"
            :placeholder="$t('orderType')"
            display="chip"
            :style="{ 'max-width': '120px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOrderTypeColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Order State -->
      <Column
        field="orderState"
        header="Status"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.orderState"
            :variant="getOrderStateColor(slotProps.data.orderState)"
          >
            {{ $t(slotProps.data.orderState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.orderStates"
            :options="orderStates"
            @input="onOrderStateFilter"
            optionLabel="label"
            placeholder="Status"
            display="chip"
            :style="{ 'max-width': '150px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getOrderStateColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Submitted to Supplier Date -->
      <Column
        field="submittedToSupplierDate"
        :header="$t('submittedToSupplierDate')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="submittedToSupplierDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.submittedToSupplierDate">
            {{ formatDate(slotProps.data.submittedToSupplierDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Expected Delivery -->
      <Column
        field="expectedDeliveryDate"
        :header="$t('expectedDeliveryDate')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        filterField="expectedDeliveryDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.expectedDeliveryDate">
            {{ formatDate(slotProps.data.expectedDeliveryDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Actual Delivery -->
      <Column
        field="actualDeliveryDate"
        :header="$t('actualDeliveryDate')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        filterField="actualDeliveryDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.actualDeliveryDate">
            {{ formatDate(slotProps.data.actualDeliveryDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Created By -->
      <Column field="createdBy" :header="$t('createdBy')" sortable filterField="createdBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdBy">
            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}<br />
            <small>{{ slotProps.data.createdBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Total Cost -->
      <Column
        field="totalCost"
        filterField="totalCost"
        :header="$t('total_cost')"
        sortable
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.totalCost">
            {{ formatCurrency(slotProps.data.totalCost) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('total_cost')"
          />
        </template>
      </Column>

      <!-- Updated By -->
      <Column field="updatedBy" :header="$t('updatedBy')" sortable filterField="updatedBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.updatedBy">
            {{ slotProps.data.updatedBy.firstname }} {{ slotProps.data.updatedBy.lastname }}<br />
            <small>{{ slotProps.data.updatedBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button class="btn" @click="$emit('edit-order', slotProps.data)" title="Edit Order">
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import dayjs from 'dayjs';
import Calendar from 'primevue/calendar';
import { getOrderStateColor, getOrderPriorityColor, getOrderTypeColor } from '@/helpers/colors';
import ButtonWait from '@/components/ButtonWait.vue';

export default {
  name: 'OrdersTable',
  components: {
    DataTable,
    InputText,
    Column,
    MultiSelect,
    Calendar,
    ButtonWait,
  },

  props: {
    /** Number of the project whose block shows the table; the server forces it. */
    projectNumber: {
      type: Number,
      default: null,
    },
    tableStatePrefix: {
      type: String,
      default: 'orders',
    },
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
    createOrderRoute: {
      type: Object,
      default: () => ({ name: 'OrderCreatePage' }),
    },
    /** Viewer on the view orders: lists and exports, cannot create. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    /** Whether the order number opens the view orders; without a level on it, plain text. */
    canOpenOrderPage: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      orderTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          number: { value: null },
          supplier: { value: null },
          supplierOrderNumber: { value: null },
          projectSearch: { value: null },
          submittedToSupplierDateRange: { range: null },
          expectedDeliveryDateRange: { range: null },
          actualDeliveryDateRange: { range: null },
          createdBy: { value: null },
          updatedBy: { value: null },
          totalCost: { value: null },
        },
        customFilters: {
          orderStates: [],
          priorities: [],
          orderTypes: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 }, // Latest orders first
      },
      defaultFilters: {
        number: { value: null },
        supplier: { value: null },
        supplierOrderNumber: { value: null },
        projectSearch: { value: null },
        submittedToSupplierDateRange: { range: null },
        expectedDeliveryDateRange: { range: null },
        actualDeliveryDateRange: { range: null },
        createdBy: { value: null },
        updatedBy: { value: null },
        totalCost: { value: null },
      },
      defaultCustomFilters: {
        orderStates: [],
        priorities: [],
        orderTypes: [],
      },
    };
  },

  computed: {
    ...mapGetters(['getOrders', 'getOrderCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),
    ...mapGetters('auth', ['getCurrentUser']),

    tableStateStore() {
      return this.getTableState(this.orderTableStateConfig.name);
    },

    orderStates() {
      const states = [];
      this.getEnumValues('OrderState').map((state) => {
        states.push({ value: state, label: this.$t(state) });
      });
      return states;
    },

    priorities() {
      const priorities = [];
      this.getEnumValues('OrderPriority').map((priority) => {
        priorities.push({ value: priority, label: this.$t(priority) });
      });
      return priorities;
    },

    orderTypes() {
      const types = [];
      this.getEnumValues('OrderType').map((type) => {
        types.push({ value: type, label: this.$t(type) });
      });
      return types;
    },
  },

  methods: {
    ...mapActions([
      'initTableState',
      'fetchEnumValues',
      'fetchUserAttachmentUrl',
      'ordersExport',
      'fetchOrdersPaginated',
    ]),
    ...mapActions('auth', ['refreshUser']),

    async startOrdersExport() {
      if (this.getCurrentUser) {
        const userId = this.getCurrentUser.id;

        await this.ordersExport({
          userId,
          projectNumber: this.projectNumber,
          pagination: {
            skip: this.tableStateStore.skip(),
            pageSize: this.tableStateStore.pagination.pageSize,
          },
          sorting: this.tableStateStore.sorting,
          filters: {
            ...this.tableStateStore.filters,
            ...this.tableStateStore.customFilters,
          },
        });
      }
    },

    async fetchExportBuildState() {
      await this.refreshUser();

      if (this.getCurrentUser.ordersExportBuildState === 'FINISHED') {
        this.downloadOrdersExport('ordersExportAttachment');
      }
      return this.getCurrentUser.ordersExportBuildState;
    },

    async downloadOrdersExport(attachmentType) {
      try {
        const url = await this.fetchUserAttachmentUrl({
          userId: this.getCurrentUser.id,
          attachmentType: attachmentType,
        });
        window.open(url);
      } catch (error) {
        console.error('Error fetching attachment URL:', error);
      }
    },

    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters };

      await this.fetchOrdersPaginated({
        projectNumber: this.projectNumber,
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: filters,
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Filter by order states
     */
    async onOrderStateFilter(value) {
      const filteredStates = {
        orderStates: this.orderStates.filter((state) => value.includes(state)),
      };
      this.tableStateStore.setCustomFilters(filteredStates);
      await this.loadTableData();
    },

    /**
     * Filter by priorities
     */
    async onPriorityFilter(value) {
      const filteredPriorities = {
        priorities: this.priorities.filter((priority) => value.includes(priority)),
      };
      this.tableStateStore.setCustomFilters(filteredPriorities);
      await this.loadTableData();
    },

    /**
     * Filter by order types
     */
    async onOrderTypeFilter(value) {
      const filteredTypes = {
        orderTypes: this.orderTypes.filter((type) => value.includes(type)),
      };
      this.tableStateStore.setCustomFilters(filteredTypes);
      await this.loadTableData();
    },

    // Import color functions from helpers
    getOrderStateColor,
    getOrderPriorityColor,
    getOrderTypeColor,

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD.MM.YYYY');
    },

    /**
     * Format currency for display
     */
    formatCurrency(amount) {
      if (!amount) return '-';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount);
    },

    /**
     * Action handlers
     */
    viewOrder(order) {
      this.$router.push({
        name: 'OrderEditPage',
        params: { orderNumber: order.number },
      });
    },

    editOrder(order) {
      this.$router.push({
        name: 'OrderEditPage',
        params: { orderNumber: order.number },
      });
    },

    deleteOrder(order) {
      console.log('Delete order:', order);
      // TODO: Implement delete order functionality
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.orderTableStateConfig);
  },

  async mounted() {
    await this.fetchEnumValues('OrderState');
    await this.fetchEnumValues('OrderPriority');
    await this.fetchEnumValues('OrderType');
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
